import { hashKey } from "@tanstack/react-query";
import { sum } from "@common/helpers/collections";
import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import {
    ActivitySeries,
    HistoryCountsBreakdown,
} from "@common/schemas/domains/activity/activity-timeline.schema";
import type {
    HistoryCountsPeopleBreakdown,
    HistoryCountsSeries,
    HistoryCountsSeriesBreakdown,
    HistoryDailyCount,
    HistoryDailyCountsQuery,
    HistoryDailyCountsResponse,
    HistoryFilter,
} from "@common/types/auditLog";
import { ActorType } from "@common/types/auditLog";
import type { ChunkPlaceholder } from "@api/combineChunkQueries";
import { toPickedPersonIds } from "./activityTimelinePeople.lib";
import type { TimelineSeriesKey } from "./activityTimelineSeries.lib";
import { toSeriesCounts } from "./activityTimelineSeries.lib";

const NO_COUNTS: HistoryCountsSeries = { total: 0, outcomes: {} };

/** A filter's Updated by fields. */
export type ActorPickFilter = Pick<
    HistoryFilter,
    "actorIds" | "actorType" | "sourceTypes" | "sourcePredicates" | "series"
>;

/** Updated by's picks, which the counts match any of; no pick and an empty one read the same. */
export const toActorPicks = (filters: ActorPickFilter | undefined) => ({
    actorIds: filters?.actorIds ?? [],
    actorType: filters?.actorType,
    sourceTypes: filters?.sourceTypes ?? [],
    sourcePredicates: filters?.sourcePredicates ?? [],
    series: filters?.series ?? [],
});

type ActorPicks = ReturnType<typeof toActorPicks>;

/** Picks besides people that can count people's work, which People with picked people would drop. */
const canCountPeopleWork = (picks: ActorPicks) =>
    picks.sourceTypes.length > 0 ||
    picks.sourcePredicates.length > 0 ||
    (picks.actorType !== undefined && picks.actorType !== ActorType.SUPPORT);

/** Everything but Updated by and the outcome, which a derived chunk shares with the one it's derived from. */
const toScopeKey = ({ filters, bucket, timeZone }: HistoryDailyCountsQuery) =>
    hashKey([
        {
            ...filters,
            actorIds: undefined,
            actorType: undefined,
            sourceTypes: undefined,
            sourcePredicates: undefined,
            series: undefined,
            outcome: undefined,
        },
        bucket,
        timeZone,
    ]);

interface KeptSeries {
    key: TimelineSeriesKey | undefined;
    /** False when picked people's changes may also land in Rules or Kick, which `people` doesn't count. */
    isExact: boolean;
}

/**
 * What new picks keep of the old picks' counts: every series when they're
 * the same, else the person or people they count, which the old picks must
 * have counted too. Undefined when the counts can't tell.
 */
function toKeptSeries({
    from,
    to,
    fromBreakdown,
}: {
    from: ActorPicks;
    to: ActorPicks;
    fromBreakdown: HistoryCountsBreakdown | undefined;
}): KeptSeries | undefined {
    if (hashKey([from]) === hashKey([to])) {
        return { key: undefined, isExact: true };
    }
    if (
        to.actorType !== undefined ||
        to.sourceTypes.length > 0 ||
        to.sourcePredicates.length > 0 ||
        to.series.length > 0
    ) {
        return undefined;
    }
    const personIds = toPickedPersonIds(to);
    const fromPersonIds = toPickedPersonIds(from);
    const [personId, ...otherIds] = personIds;
    if (personId === undefined || personIds.length !== to.actorIds.length) {
        return undefined;
    }
    if (
        otherIds.length === 0 &&
        fromBreakdown === HistoryCountsBreakdown.SERIES_AND_PEOPLE &&
        fromPersonIds.includes(personId)
    ) {
        return {
            key: { series: ActivitySeries.PEOPLE, personId },
            isExact: false,
        };
    }
    const isSamePeople =
        personIds.length === fromPersonIds.length &&
        personIds.every((id) => fromPersonIds.includes(id));
    return isSamePeople && !canCountPeopleWork(from)
        ? { key: { series: ActivitySeries.PEOPLE }, isExact: false }
        : undefined;
}

/** The outcome's count as the total, and as the only outcome: the counts don't say which other outcomes its changes also match. */
const toClickedOutcomeOnly = (
    counts: HistoryCountsSeries,
    outcome: ActivityOutcomeKind | undefined,
): HistoryCountsSeries => {
    if (outcome === undefined) {
        return counts;
    }
    const total = counts.outcomes[outcome] ?? 0;
    return { total, outcomes: total > 0 ? { [outcome]: total } : {} };
};

interface Narrowing {
    /** The one series or person kept; every series when undefined. */
    key: TimelineSeriesKey | undefined;
    /** The one outcome kept; every outcome when undefined. */
    outcome: ActivityOutcomeKind | undefined;
    breakdown: HistoryCountsBreakdown;
}

function toNarrowedPeople({
    people,
    key,
    outcome,
}: Omit<Narrowing, "breakdown"> & {
    people: HistoryCountsPeopleBreakdown;
}): HistoryCountsPeopleBreakdown {
    if (key && key.series !== ActivitySeries.PEOPLE) {
        return {};
    }
    return Object.fromEntries(
        Object.entries(people).flatMap(
            ([id, counts]): Array<[string, HistoryCountsSeries]> => {
                const narrowed = toClickedOutcomeOnly(counts, outcome);
                const isKept =
                    (key?.personId === undefined ||
                        id === String(key.personId)) &&
                    narrowed.total > 0;
                return isKept ? [[id, narrowed]] : [];
            },
        ),
    );
}

/** The row narrowed, or undefined when nothing is left, as the counts leave out buckets with nothing in them. */
function toNarrowedRow({
    row,
    key,
    outcome,
    breakdown,
}: Narrowing & { row: HistoryDailyCount }): HistoryDailyCount | undefined {
    const toCounts = (seriesKey: TimelineSeriesKey) =>
        toClickedOutcomeOnly(
            toSeriesCounts(row, seriesKey) ?? NO_COUNTS,
            outcome,
        );
    const toKeptCounts = (kept: ActivitySeries) => {
        if (key === undefined) {
            return toCounts({ series: kept });
        }
        return key.series === kept ? toCounts(key) : NO_COUNTS;
    };
    const narrowedSeries: HistoryCountsSeriesBreakdown = {
        [ActivitySeries.PEOPLE]: toKeptCounts(ActivitySeries.PEOPLE),
        [ActivitySeries.KICK]: toKeptCounts(ActivitySeries.KICK),
        [ActivitySeries.INTEGRATIONS]: toKeptCounts(
            ActivitySeries.INTEGRATIONS,
        ),
        [ActivitySeries.RULES]: toKeptCounts(ActivitySeries.RULES),
    };
    // `count` sums the History list's rows, which counts can't recover; the chart doesn't read it.
    const count = sum(Object.values(narrowedSeries).map(({ total }) => total));
    if (count === 0) {
        return undefined;
    }
    return {
        date: row.date,
        count,
        series: narrowedSeries,
        ...(breakdown === HistoryCountsBreakdown.SERIES_AND_PEOPLE && {
            people: toNarrowedPeople({
                people: row.people ?? {},
                key,
                outcome,
            }),
        }),
    };
}

/**
 * The bars `query` gets, derived from a chunk's counts for a wider filter
 * when it only narrows Updated by to a person or the picked people, as a
 * click on a person or People does, or adds an outcome: People as counted
 * for them, the others zero, and with an outcome each count its outcome's.
 * Narrowing to people keeps only their People work: their rule-driven and
 * Kick changes also count, so those bars aren't exact. With an added
 * outcome, each `outcomes` lists only that outcome until the response lands,
 * which also lists the other outcomes those changes match. `count` is the
 * totals' sum. Undefined when anything else changed, or the shown counts
 * didn't count the kept people.
 */
export function toNarrowedCounts({
    shown: { query: from, data },
    query: to,
}: {
    shown: { query: HistoryDailyCountsQuery; data: HistoryDailyCountsResponse };
    query: HistoryDailyCountsQuery;
}): ChunkPlaceholder<HistoryDailyCountsResponse> | undefined {
    const { breakdown } = to;
    const fromOutcome = from.filters?.outcome;
    const outcome = to.filters?.outcome;
    const hasPeople =
        from.breakdown === HistoryCountsBreakdown.SERIES_AND_PEOPLE;
    const isComparable =
        toScopeKey(from) === toScopeKey(to) &&
        (fromOutcome === undefined || fromOutcome === outcome) &&
        (hasPeople || breakdown !== HistoryCountsBreakdown.SERIES_AND_PEOPLE) &&
        data.data.every((row) => row.series !== undefined);
    const kept =
        isComparable &&
        toKeptSeries({
            from: toActorPicks(from.filters),
            to: toActorPicks(to.filters),
            fromBreakdown: from.breakdown,
        });
    if (!kept || breakdown === undefined) {
        return undefined;
    }
    const narrowing: Narrowing = {
        key: kept.key,
        outcome: fromOutcome === undefined ? outcome : undefined,
        breakdown,
    };
    return {
        data: {
            ...data,
            data: data.data.flatMap(
                (row) => toNarrowedRow({ row, ...narrowing }) ?? [],
            ),
        },
        isExact: kept.isExact,
    };
}
